import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import useClassNames from '@medflex/hooks/useClassNames';
import { IconCheveronDown, IconCheveronUp, IconQuestion } from '../../Icons';
import SessionsFAQItem from './SessionsFAQItem';
import Button from '../../Button';

const SessionsFAQ = ({
  className,
  selected: selectedOuter,
  expanded: expandedOuter,
  onStartDeviceTestClick,
  onHelpDeskSessionsClick,
  onHelpDeskAccountingClick,
}) => {
  const cn = useClassNames(() => ['text-slate-600', className], [className]);
  const { t } = useTranslation('components');
  const [selected, setSelected] = useState(selectedOuter);
  const [expanded, setExpanded] = useState(expandedOuter);

  const items = t('components:Sessions.SessionsFAQ.items', { returnObjects: true });

  const handleSelect = index => () => {
    if (selected === index) {
      setSelected(null);
      return;
    }
    setSelected(index);
  };

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  const enhanceResponse = (item, i) => {
    switch (i) {
      case 0: // help desk
        return (
          <Trans i18nKey={`components:Sessions.SessionsFAQ.items.${i}.response`}>
            <div>body</div>
            <Button kind="link" onClick={onHelpDeskSessionsClick}>
              Help Desk: Sessions
            </Button>
          </Trans>
        );
      case 2: // KV registration
        return (
          <Trans i18nKey={`components:Sessions.SessionsFAQ.items.${i}.response`}>
            <div>body</div>
            <Button kind="link" onClick={onHelpDeskAccountingClick}>
              Help Desk: Insurance Notice
            </Button>
          </Trans>
        );
      case 3: // device test
        return (
          <Trans i18nKey={`components:Sessions.SessionsFAQ.items.${i}.response`}>
            <div>body</div>
            <Button kind="link" onClick={onStartDeviceTestClick}>
              Start Device Test
            </Button>
          </Trans>
        );
      default:
        return item.response;
    }
  };

  const enhancedItems = useMemo(
    () =>
      items.map((item, i) => (
        <SessionsFAQItem
          key={`${item.question}${i}`}
          onClick={handleSelect(i)}
          expanded={selected === i}
          question={item.question}
          response={enhanceResponse(item, i)}
        />
      )),
    [items],
  );

  return (
    <div className={cn}>
      <h3 className="font-semibold text-xl inline-flex items-center">
        <IconQuestion size="text" />
        <span className="ml-1">{t('Sessions.SessionsFAQ.title')}</span>
      </h3>
      <div className="bg-blue-100 p-6">
        <ol className="list-decimal list-outside ml-4 mb-4 space-y-2" hidden={!expanded}>
          {enhancedItems}
        </ol>
        <button
          type="button"
          className="focus:outline-none flex items-center"
          onClick={toggleExpanded}
        >
          {expanded ? (
            <>
              <IconCheveronUp />
              <span className="ml-1">{t('Sessions.SessionsFAQ.collapse')}</span>
            </>
          ) : (
            <>
              <IconCheveronDown />
              <span className="ml-1">{t('Sessions.SessionsFAQ.expand')}</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

SessionsFAQ.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.array,
    PropTypes.object,
  ]),
  selected: PropTypes.bool,
  expanded: PropTypes.bool,
  onStartDeviceTestClick: PropTypes.func,
  onHelpDeskSessionsClick: PropTypes.func,
  onHelpDeskAccountingClick: PropTypes.func,
};

SessionsFAQ.defaultProps = {
  selected: null,
  expanded: false,
  onStartDeviceTestClick: () => {},
  onHelpDeskSessionsClick: () => {},
  onHelpDeskAccountingClick: () => {},
};

export default SessionsFAQ;
